import { useState } from 'react';
import { X, HelpCircle, Target, BarChart3, Calculator, Layers, Shuffle, Scale, Zap, TrendingUp, Percent, BookOpen, Shield, Users, Play, GitBranch, Clock, ArrowUpDown, Newspaper, Calendar, ShoppingBag, Store, Sparkles, Beaker } from 'lucide-react';
import { SubNavDropdown } from './SubNavDropdown';

interface FAQItem {
    id: string;
    icon: React.ReactNode;
    title: string;
    brief: string;
    details: string | string[];
    color: string;
    category: 'core' | 'analytics' | 'community' | 'legal';
}

const faqItems: FAQItem[] = [
    // #1  What is DECKSTR
    {
        id: 'what-is-deckstr',
        icon: <HelpCircle className="size-6" />,
        title: 'What is DECKSTR?',
        brief: 'The relationship between two curves',
        details: 'DECKSTR analyzes the difference between two statistical distributions: your nonlands\' curve responsibility and your lands\' mana production curve. Think of it like a see-saw - your spells want mana, your lands provide it. DECKSTR helps you find the balance point where your draws are both castable AND impactful. Example: If your deck has too many 5-drops and not enough lands, your curve is "heavy" on top.',
        color: 'cyan',
        category: 'core',
    },
    // #2  The Three-Variable Equation
    {
        id: 'three-variable-equation',
        icon: <Calculator className="size-6" />,
        title: 'The Three-Variable Equation',
        brief: 'Lands, Ratio, and Curve are locked together',
        details: 'Every deck obeys one equation: Curve = (Lands x Ratio) / Nonlands. Change any one variable and the other two must adjust - they are not independent. Targeting a Curve of 3.0 across formats: Limited (17 lands in 40) needs Ratio 4.06. Constructed (24 lands in 60) needs Ratio 4.50. EDH (36 lands in 99) needs Ratio 5.25. Same target curve, but fewer lands per card means each land must carry more weight. This is the fundamental constraint behind every deck regardless of format. Lands set your draw frequency, Ratio sets the mana budget each land supports, and Curve is the result. Know two, derive the third.',
        color: 'indigo',
        category: 'core',
    },
    // News & Events
    {
        id: 'news-tab',
        icon: <Newspaper className="size-6" />,
        title: 'NEWS Tab',
        brief: 'MTG news from across the web',
        details: 'The NEWS tab aggregates Magic: The Gathering news from major sources across the web \u2014 set announcements, ban updates, tournament results, and community content. Articles are auto-fetched and displayed in a clean feed so you can stay up to date without leaving DECKSTR.GG. Filter by source or browse the latest headlines while you brew.',
        color: 'blue',
        category: 'community',
    },
    {
        id: 'events-tab',
        icon: <Calendar className="size-6" />,
        title: 'EVENTS Tab',
        brief: 'Find and host MTG events',
        details: 'The EVENTS tab lets you discover upcoming Magic events \u2014 from local FNMs to major tournaments. Event organizers can create and manage events with registration, decklists, and rosters. Players can register with their decklists directly through DECKSTR.GG. Filter by format, date, or location to find events near you.',
        color: 'pink',
        category: 'community',
    },
    // Core Concepts
    {
        id: 'deck-weight',
        icon: <Layers className="size-6" />,
        title: 'Deck Weight',
        brief: 'Total mana burden of your spells',
        details: 'Deck Weight is the sum of all mana values in your deck. Think of it like the total "cost" your lands need to pay over a game. Example: 4\u00d7 Lightning Bolt (1 mana each) + 4\u00d7 Counterspell (2 each) + 2\u00d7 Jace (4 each) = 4 + 8 + 8 = 20 weight. Your lands must "fund" this weight. More weight = you need more lands OR a longer game.',
        color: 'amber',
        category: 'core',
    },
    {
        id: 'anchor-distribution',
        icon: <Target className="size-6" />,
        title: 'Anchor Distribution',
        brief: 'Your ideal card counts per MV',
        details: 'The anchor is a bell-curve showing exactly how many cards belong at each mana value. It\'s your deck\'s "blueprint." Example: For a 2.5 curve with 24 lands, anchor might say: MV1=4, MV2=8, MV3=8, MV4=6, MV5=4, MV6=2. If you want to play jank combos or specific commanders, you adjust YOUR deck - the anchor gives you the statistically optimal baseline to compare against.',
        color: 'pink',
        category: 'core',
    },
    {
        id: 'ratio-wpl',
        icon: <Scale className="size-6" />,
        title: 'Ratio (WPL)',
        brief: 'Mana responsibility per land',
        details: 'Weight Per Land (WPL) = Total Weight \u00f7 Lands. It tells you how much "work" each land does. Example: 72 weight \u00f7 24 lands = 3.0 ratio. This means each land "pays for" 3 mana of spells. Aggro decks aim for 2.0-2.5 (cheap spells). Control decks can push 3.5+ (powerful late plays). If your ratio is too high, you\'ll flood; too low, you\'ll brick.',
        color: 'purple',
        category: 'core',
    },
    {
        id: 'floor-peak',
        icon: <ArrowUpDown className="size-6" />,
        title: 'Floor & Peak',
        brief: 'Lock your curve boundaries',
        details: 'Floor sets the lowest mana value your deck starts at. Peak sets the highest. These lock the anchor distribution to only generate card slots within your chosen range. Example: Setting Floor=1 and Peak=4 means no 0-drops and nothing above 4-drops. Cards that would have been placed outside these bounds are folded into the boundary slots, preserving total count and weight. Use Floor to skip cheap slots in ramp-heavy decks, or Peak to cap your curve for aggro builds.',
        color: 'red',
        category: 'core',
    },
    // Analytics
    {
        id: 'mana-curve',
        icon: <TrendingUp className="size-6" />,
        title: 'Mana Curve',
        brief: 'Average spell cost',
        details: 'Your curve = Total Weight \u00f7 Number of Spells. It\'s the "center of gravity" of your deck. Example: A Red Deck Wins with curve 1.8 wants to win before turn 5. A Tron deck with curve 4.2 wants to survive until it can slam Karn. Your curve tells you your deck\'s pace - are you racing or grinding?',
        color: 'green',
        category: 'analytics',
    },
    {
        id: 'probability',
        icon: <Percent className="size-6" />,
        title: 'Probability',
        brief: 'Odds of drawing what you need',
        details: 'Probability shows your chances of drawing specific cards. Example: 4 copies in a 60-card deck = ~40% chance to see at least one in your opening 7. The PROBABILITIES tab validates your build: "If I construct this curve, what are my realistic odds of curving out?" This is your stress test before you sleeve up.',
        color: 'blue',
        category: 'analytics',
    },
    {
        id: 'playability',
        icon: <Zap className="size-6" />,
        title: 'Playability',
        brief: 'Can you cast what you draw?',
        details: 'Playability = having the mana when you need it. Example: Drawing Sheoldred on turn 2 with 2 lands is "drawable" but not "playable." DECKSTR.GG optimizes for playable draws - your 2-drops should be in hand on turn 2, not your 5-drops. A well-curved deck minimizes the frustrating "got the card, can\'t cast it" moments.',
        color: 'yellow',
        category: 'analytics',
    },
    {
        id: 'effectiveness',
        icon: <BarChart3 className="size-6" />,
        title: 'Effectiveness',
        brief: 'Does it matter when played?',
        details: 'A card can be drawable and playable but still weak. Example: Casting Llanowar Elves on turn 7 uses only 1/7 of your mana (14% efficiency) - you\'re wasting 6 mana. DECKSTR.GG ensures your curve gives you impactful plays each turn: early game = cheap interaction, late game = haymakers. Every draw should MATTER.',
        color: 'orange',
        category: 'analytics',
    },
    // Features
    {
        id: 'brew-vs-probs',
        icon: <BookOpen className="size-6" />,
        title: 'BREW vs PROBABILITIES',
        brief: 'Blueprint vs Stress Test',
        details: 'BREW tab = "Put X cards at each MV" - it\'s your construction zone. PROBABILITIES tab = "What are my odds of curving out?" - it\'s your validation suite. Example: BREW tells you "run 8 two-drops." PROBABILITIES shows "with 8 two-drops, you have 73% chance of hitting one by turn 2." Build in BREW, verify in PROBABILITIES.',
        color: 'teal',
        category: 'community',
    },
    {
        id: 'match-plans',
        icon: <GitBranch className="size-6" />,
        title: 'Match Plans & Branching',
        brief: 'Sideboard swaps + what-if analysis',
        details: 'DECKSTR tracks 5 match plans (A-E) so you can see exactly how sideboard swaps shift your curve. Example: Base deck = curve 2.8. Plan B (vs control) boards in haymakers = curve 3.2. Plan C (vs aggro) boards in cheap removal = curve 2.4. Coming soon: What-If Branching lets you fork from any turn during playtesting, try different lines, and compare outcomes side-by-side with auto-labeled branches.',
        color: 'emerald',
        category: 'community',
    },
    // Discover, Market & Session
    {
        id: 'market-tab',
        icon: <Store className="size-6" />,
        title: 'MARKET Tab',
        brief: 'LGS finder, card marketplace & sparks trading',
        details: 'The MARKET tab is your hub for everything commerce. Find local game stores (LGS) in your area using the WPN Store Locator. Browse the Sparks Marketplace to trade cards using a combination of cash and sparks \u2014 the DECKSTR merit currency. Scan your physical cards with your webcam or phone camera to add them to your collection, set condition/quantity, and list them for trade. Card prices are ceiling-capped by TCGPlayer market data so nobody can inflate values.',
        color: 'amber',
        category: 'community',
    },
    {
        id: 'sparks-economy',
        icon: <Zap className="size-6" />,
        title: 'Sparks \u2014 The Merit Economy',
        brief: 'Earn trading power through community engagement',
        details: [
            'Sparks (\u2728) are DECKSTR\'s merit-based currency earned through genuine community engagement. Each spark \u2248 $0.50 and can be used for hybrid trades (cash + sparks) in the Marketplace.',
            '\ud83c\udfaf ONBOARDING: First sign-in unlocks one-time rewards for exploring each tab \u2014 News (+1\u2728), Events (+1\u2728), Save a Deck (+2\u2728), Discover (+1\u2728), Market (+1\u2728), Session (+1\u2728), FAQ (+1\u2728), plus +10\u2728 for linking Twitch.',
            '\ud83d\udcb0 DAILY CAP (5\u2728/day): Sign-in +1, save deck +2, upvote decks +2, brew on stream +5, watch streams +1. A $50 card takes ~3 weeks of real engagement.',
            '\ud83d\udcca PRICING: Card values are system-set from TCGPlayer market data \u00d7 condition multiplier. Nobody can list above the ceiling.',
            '\ud83d\udd14 ISO ALERTS: Mark cards you want. When listed, you get 24h priority bidding via email + in-app notification.',
            '\ud83d\udee1\ufe0f ANTI-ABUSE: IP rate limits, Twitch verification, three-point card scan verification.'
        ],
        color: 'amber',
        category: 'community',
    },
    {
        id: 'session-tab',
        icon: <Play className="size-6" />,
        title: 'SESSION Tab',
        brief: 'Playtesting and webcam integration',
        details: 'The SESSION tab provides goldfish playtesting with life tracking and webcam integration for SpellTable-style streaming. Features: Mull to X, shuffle, draw controls, and real-time Supabase sync for OBS overlays. Multiple player support with format presets (Commander, Standard, etc.).',
        color: 'green',
        category: 'community',
    },
    // Legal
    {
        id: 'legal-disclaimer',
        icon: <Shield className="size-6" />,
        title: 'Legal Disclaimer',
        brief: '100% community-made, fan project',
        details: 'DECKSTR.GG is a fan-made community project by Twitch streamers and players. We are NOT affiliated with, endorsed by, or associated with Wizards of the Coast, Hasbro, or Magic: The Gathering in any way. This is a free tool made by fans, for fans, with no intention to infringe on any intellectual property. We simply love the game and want to help players improve their deck-building experience.',
        color: 'slate',
        category: 'legal',
    },
];

const categoryLabels: Record<string, { label: string; description: string }> = {
    core: { label: 'Core Concepts', description: 'Fundamental DECKSTR methodology' },
    analytics: { label: 'Analytics', description: 'Understanding your numbers' },
    community: { label: 'Features', description: 'Platform capabilities' },
    legal: { label: 'Legal', description: 'Important notices' },
};

export function FAQPage() {
    const [selectedItem, setSelectedItem] = useState<FAQItem | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>('all');

    const colorClasses: Record<string, { bg: string; border: string; text: string; glow: string }> = {
        cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', glow: 'hover:shadow-cyan-500/20' },
        purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'hover:shadow-purple-500/20' },
        amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', glow: 'hover:shadow-amber-500/20' },
        green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', glow: 'hover:shadow-green-500/20' },
        pink: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-400', glow: 'hover:shadow-pink-500/20' },
        blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', glow: 'hover:shadow-blue-500/20' },
        yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', glow: 'hover:shadow-yellow-500/20' },
        orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', glow: 'hover:shadow-orange-500/20' },
        red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', glow: 'hover:shadow-red-500/20' },
        indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', glow: 'hover:shadow-indigo-500/20' },
        teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', glow: 'hover:shadow-teal-500/20' },
        emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', glow: 'hover:shadow-emerald-500/20' },
        slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-400', glow: 'hover:shadow-slate-500/20' },
    };

    const filteredItems = activeCategory === 'all'
        ? faqItems
        : faqItems.filter(item => item.category === activeCategory);

    const categories = ['all', 'core', 'analytics', 'community', 'legal'];

    return (
        <div className="min-h-full p-4 sm:p-6">
            {/* Header row: subtitle + category filter */}
            <div className="flex items-center justify-between mb-4 max-w-6xl mx-auto">
                <p className="text-white/50 text-sm uppercase tracking-widest font-medium">
                    Click any topic to learn more
                </p>
                <SubNavDropdown
                    items={[
                        { key: 'all', label: 'All Topics', icon: HelpCircle, color: 'text-amber-400', count: faqItems.length },
                        { key: 'core', label: 'Core Concepts', icon: Calculator, color: 'text-cyan-400', count: faqItems.filter(i => i.category === 'core').length },
                        { key: 'analytics', label: 'Analytics', icon: BarChart3, color: 'text-green-400', count: faqItems.filter(i => i.category === 'analytics').length },
                        { key: 'community', label: 'Features', icon: BookOpen, color: 'text-purple-400', count: faqItems.filter(i => i.category === 'community').length },
                        { key: 'legal', label: 'Legal', icon: Shield, color: 'text-slate-400', count: faqItems.filter(i => i.category === 'legal').length },
                    ]}
                    activeKey={activeCategory}
                    onSelect={setActiveCategory}
                />
            </div>

            {/* Grid */}
            <div className="perspective-container">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                    {filteredItems.map((item) => {
                        const colors = colorClasses[item.color] || colorClasses.cyan;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setSelectedItem(item)}
                                className={`
                                    card-3d ${colors.border} border-2 rounded-2xl p-5 sm:p-6
                                    text-left transition-all duration-300 
                                    hover:shadow-2xl ${colors.glow} 
                                    group relative overflow-hidden
                                `}
                                style={{
                                    background: `linear-gradient(165deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 30%, rgba(0,0,0,0.15) 100%)`,
                                    boxShadow: `
                                        0 8px 24px -4px rgba(0, 0, 0, 0.5),
                                        0 16px 40px -8px rgba(0, 0, 0, 0.3),
                                        inset 0 1px 0 rgba(255, 255, 255, 0.12),
                                        inset 0 -1px 0 rgba(0, 0, 0, 0.3)
                                    `,
                                }}
                            >
                                {/* Top edge highlight */}
                                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                {/* Bottom edge shadow */}
                                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-black/30 to-transparent" />
                                {/* Inner glow on hover */}
                                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />

                                <div className={`${colors.text} mb-3 transition-transform group-hover:scale-110 relative z-10`}>
                                    {item.icon}
                                </div>
                                <h3 className="text-white font-semibold text-sm sm:text-base mb-1 relative z-10">{item.title}</h3>
                                <p className="text-white/50 text-xs sm:text-sm line-clamp-2 relative z-10">{item.brief}</p>

                                {/* Coming Soon badge for future features */}
                                {item.details.includes('Coming Soon') && (
                                    <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] bg-indigo-500/30 text-indigo-300 rounded-full z-10">
                                        Soon
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Popup Modal */}
            {selectedItem && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    onClick={() => setSelectedItem(null)}
                >
                    <div
                        className={`
                            panel-3d
                            ${colorClasses[selectedItem.color]?.bg || 'bg-slate-800'} 
                            ${colorClasses[selectedItem.color]?.border || 'border-white/20'} 
                            border rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl max-h-[85vh] overflow-y-auto
                            transform transition-all animate-in fade-in zoom-in-95 duration-200
                        `}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`${colorClasses[selectedItem.color]?.text || 'text-white'}`}>
                                    {selectedItem.icon}
                                </div>
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-bold text-white">{selectedItem.title}</h2>
                                    <span className="text-xs text-white/40 uppercase tracking-wider">
                                        {categoryLabels[selectedItem.category]?.label}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedItem(null)}
                                className="text-white/40 hover:text-white transition-colors p-1"
                            >
                                <X className="size-5" />
                            </button>
                        </div>

                        {/* Brief */}
                        <p className={`${colorClasses[selectedItem.color]?.text || 'text-cyan-400'} text-sm font-medium mb-4`}>
                            {selectedItem.brief}
                        </p>

                        {/* Details — supports string or string[] for multi-paragraph */}
                        <div className="text-white/80 text-sm sm:text-base leading-relaxed space-y-3">
                            {(Array.isArray(selectedItem.details)
                                ? selectedItem.details
                                : [selectedItem.details]
                            ).map((para: string, i: number) => (
                                <p key={i}>{para}</p>
                            ))}
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => setSelectedItem(null)}
                            className={`
                                mt-6 w-full py-2.5 rounded-lg 
                                ${colorClasses[selectedItem.color]?.bg || 'bg-white/10'} 
                                ${colorClasses[selectedItem.color]?.border || 'border-white/20'} 
                                border ${colorClasses[selectedItem.color]?.text || 'text-white'} 
                                font-medium hover:bg-white/10 transition-colors
                            `}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
